import { QUESTION_TYPES } from '@api/instant_survey';

import { QUESTION_GROUP_DROPPABLE_PREFIX } from '../components/QuestionGroupBlock';

/**
 * buildRenderList
 * Collapses consecutive questions that share a questionGroup into a single group entry.
 * Ungrouped questions and SequentialMonadic questions are passed through as-is.
 *
 * Returns an array of items shaped:
 *   { type: 'question', question, flatIndex } | { type: 'group', groupId, questions, flatIndexStart }
 *
 * flatIndex(Start) is the index of the item (or first item of the group) in the flat questions array
 * — used for drag-and-drop positional math.
 *
 * When `enableGroups` is false (QUESTION_GROUP_BLOCKS gate off), grouping is skipped entirely and
 * every question is rendered flat, even if it already carries questionGroup data.
 */
export const buildRenderList = (questions, { enableGroups = true } = {}) => {
  const isGroupable = (question) =>
    enableGroups &&
    question.questionGroup != null &&
    question.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value;

  const items = [];
  let i = 0;
  while (i < questions.length) {
    const question = questions[i];

    if (!isGroupable(question)) {
      items.push({ flatIndex: i, question, type: 'question' });
      i += 1;
    } else {
      // Collect all consecutive groupable questions with the same groupId
      const groupId = question.questionGroup;
      const groupQuestions = [];
      const flatIndexStart = i;
      while (
        i < questions.length &&
        isGroupable(questions[i]) &&
        questions[i].questionGroup === groupId
      ) {
        groupQuestions.push(questions[i]);
        i += 1;
      }
      items.push({ flatIndexStart, groupId, questions: groupQuestions, type: 'group' });
    }
  }
  return items;
};

/**
 * getGroupIdFromDroppableId
 * Returns the numeric group id embedded in a question-group droppable id, or null.
 */
export const getGroupIdFromDroppableId = (droppableId = '') => {
  const prefix = `${QUESTION_GROUP_DROPPABLE_PREFIX}-`;
  if (!droppableId.startsWith(prefix)) return null;
  const id = Number(droppableId.slice(prefix.length));
  return isNaN(id) ? null : id;
};
